import React, { forwardRef, type ChangeEvent } from 'react';

import classnames from 'classnames';

import { CALENDAR_ICON } from '../../../../dataTestIds';
import { Icon } from '../../Icon';
import { Input, type InputBaseProps } from '../../Input';

import styles from './DatePickerCalendarCustomInput.module.scss';

type DatePickerCalendarCustomInputProps = {
    value: string;
    selectsRange?: boolean;
    showSelectRangeValue?: boolean;
    readOnly?: boolean;
    className?: string;
    onChange?: (event: Omit<ChangeEvent, 'target'> & { target: { value: Date | string } }) => void;
    // This is injected by react-datepicker / Calendar via `customInputRef`
    datePickerCalendarCustomInputRef?: unknown;
} & InputBaseProps;

const DatePickerCalendarCustomInput = forwardRef<
    HTMLInputElement,
    DatePickerCalendarCustomInputProps
>(
    (
        {
            value: valueProp,
            selectsRange,
            showSelectRangeValue = false,
            readOnly,
            className,
            error,
            onChange,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            datePickerCalendarCustomInputRef: _datePickerCalendarCustomInputRef,
            ...rest
        }: DatePickerCalendarCustomInputProps,
        ref
    ) => {
        // external formatted value from DatePicker / ReactDatePicker
        const value = selectsRange && !showSelectRangeValue ? '' : valueProp ?? '';

        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            onChange?.(event);
        };

        return (
            <Input
                {...rest}
                ref={ref}
                readOnly={readOnly}
                type="text"
                error={error}
                value={value}
                suffix={
                    !readOnly && (
                        <Icon
                            data-testid={CALENDAR_ICON}
                            name="s-icon-calendar-dates"
                            size="medium"
                            color={error ? 'danger' : 'secondary'}
                            onMouseDown={rest.onMouseDown}
                        />
                    )
                }
                className={classnames(className, styles.input, {
                    [styles.range]: selectsRange
                })}
                onChange={handleChange}
            />
        );
    }
);

DatePickerCalendarCustomInput.displayName = 'DatePickerCalendarCustomInput';

export { DatePickerCalendarCustomInput };
